import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { CloudDownload, Filter, Download, X } from 'lucide-react';
import { ENV_META, EnvBadge, fmtAmt, fmtDate } from './helpers';

const API = process.env.REACT_APP_BACKEND_URL;

export const QueryTab = ({ tokens, sessionEnv }) => {
  const { t } = useTranslation();
  const [queryDateFrom, setQueryDateFrom] = useState('');
  const [queryDateTo, setQueryDateTo] = useState('');
  const [querySubjectType, setQuerySubjectType] = useState('subject2');
  const [queryTokenId, setQueryTokenId] = useState('');
  const [queryResults, setQueryResults] = useState(null);
  const [querying, setQuerying] = useState(false);
  const [downloadRef, setDownloadRef] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadTokenId, setDownloadTokenId] = useState('');
  const [selectedQuery, setSelectedQuery] = useState(new Set());
  const [bulkQueryDownloading, setBulkQueryDownloading] = useState(false);

  const envBorder = (ENV_META[sessionEnv] || ENV_META.test).border;
  const downloadTokens = tokens.filter(t => t.permissions?.includes('download'));

  const handleQuery = async () => {
    if (!queryDateFrom || !queryDateTo) { toast.error(t('auto.misc2.selectDateRange')); return; }
    setQuerying(true);
    try {
      const r = await axios.post(`${API}/api/ksef/query`, {
        date_from: queryDateFrom, date_to: queryDateTo,
        subject_type: querySubjectType, token_id: queryTokenId || null,
      });
      if (r.data.success) {
        setQueryResults(r.data);
        setSelectedQuery(new Set());
        toast.success(`Found ${r.data.total_count || r.data.invoices?.length || 0} invoices`);
      } else {
        toast.error(r.data.error || 'Query failed');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Query failed'); }
    finally { setQuerying(false); }
  };

  const handleDownload = async (ref, tkId) => {
    const kref = ref || downloadRef;
    if (!kref) { toast.error(t('auto.misc2.enterKsefReferenceNumber')); return; }
    setDownloading(true);
    try {
      const r = await axios.post(`${API}/api/ksef/download`, {
        ksef_reference: kref, token_id: tkId || downloadTokenId || null,
      });
      if (r.data.success) {
        toast.success(t('auto.misc2.invoiceDownloadedAndSaved'));
        setDownloadRef('');
      } else {
        toast.error(r.data.error || 'Download failed');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Download failed'); }
    finally { setDownloading(false); }
  };

  const toggleQuerySelect = (ref) => {
    setSelectedQuery(prev => {
      const next = new Set(prev);
      next.has(ref) ? next.delete(ref) : next.add(ref);
      return next;
    });
  };

  const toggleAllQuery = () => {
    const all = queryResults?.invoices || [];
    setSelectedQuery(prev =>
      prev.size === all.length ? new Set() : new Set(all.map(i => i.ksefNumber))
    );
  };

  const handleBulkQueryDownload = async () => {
    const refs = [...selectedQuery];
    if (!refs.length) return;
    setBulkQueryDownloading(true);
    let ok = 0; let fail = 0;
    for (const ref of refs) {
      try {
        const r = await axios.post(`${API}/api/ksef/download`, {
          ksef_reference: ref, token_id: queryTokenId || null,
        });
        r.data.success ? ok++ : fail++;
      } catch { fail++; }
    }
    setBulkQueryDownloading(false);
    toast.success(`Downloaded: ${ok} ok${fail ? `, ${fail} failed` : ''}`);
    setSelectedQuery(new Set());
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">{t('auto.misc2.downloadInvoicesFromKsef')}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {/* Query form */}
        <div className={`p-3 rounded border ${envBorder}`}>
          <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">
            <Filter className="h-3 w-3" />{t('auto.misc2.queryInvoices')}
            <EnvBadge env={sessionEnv} small />
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">{t('auto.misc2.from')}</Label>
              <Input type="date" value={queryDateFrom} onChange={e => setQueryDateFrom(e.target.value)} className="h-8 w-36" data-testid="query-date-from" />
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc2.to')}</Label>
              <Input type="date" value={queryDateTo} onChange={e => setQueryDateTo(e.target.value)} className="h-8 w-36" data-testid="query-date-to" />
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc2.subjectType')}</Label>
              <Select value={querySubjectType} onValueChange={setQuerySubjectType}>
                <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="subject2">{t('auto.misc2.issuedToUsPurchases')}</SelectItem>
                  <SelectItem value="subject1">{t('auto.misc2.issuedByUsSales')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {downloadTokens.length > 0 && (
              <div>
                <Label className="text-xs">{t('auto.misc2.token')}</Label>
                <Select value={queryTokenId || '__auto__'} onValueChange={v => setQueryTokenId(v === '__auto__' ? '' : v)}>
                  <SelectTrigger className="h-8 w-44"><SelectValue placeholder={t('auto.misc2.autoSelect')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__auto__">{t('auto.misc2.autoSelect')}</SelectItem>
                    {downloadTokens.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name} ({t.environment})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button size="sm" onClick={handleQuery} disabled={querying} data-testid="query-btn">
              {querying ? t('auto.misc2.querying') : t('auto.misc2.query')}
            </Button>
          </div>
        </div>

        {/* Query results */}
        {queryResults && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">{queryResults.invoices?.length || 0} invoices found</span>
              {selectedQuery.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-600 font-medium">{selectedQuery.size} selected</span>
                  <Button size="sm" onClick={handleBulkQueryDownload} disabled={bulkQueryDownloading} data-testid="bulk-query-download-btn">
                    <Download className="h-3.5 w-3.5 mr-1" />
                    {bulkQueryDownloading ? 'Downloading…' : `Download ${selectedQuery.size}`}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedQuery(new Set())}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      checked={selectedQuery.size === (queryResults.invoices?.length || 0) && selectedQuery.size > 0}
                      onCheckedChange={toggleAllQuery}
                      data-testid="select-all-query"
                    />
                  </TableHead>
                  <TableHead>{t('auto.misc2.ksefNumber')}</TableHead>
                  <TableHead>{t('auto.misc2.invoice')}</TableHead>
                  <TableHead>{t('auto.misc2.sellerNip')}</TableHead>
                  <TableHead>{t('auto.misc2.net')}</TableHead>
                  <TableHead>{t('auto.misc2.gross')}</TableHead>
                  <TableHead>{t('auto.misc2.date')}</TableHead>
                  <TableHead>{t('auto.misc2.action')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(queryResults.invoices || []).map(inv => (
                  <TableRow key={inv.ksefNumber || inv.ksef_number}>
                    <TableCell>
                      <Checkbox
                        checked={selectedQuery.has(inv.ksefNumber || inv.ksef_number)}
                        onCheckedChange={() => toggleQuerySelect(inv.ksefNumber || inv.ksef_number)}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{(inv.ksefNumber || inv.ksef_number || '').slice(0, 30)}…</TableCell>
                    <TableCell>{inv.invoiceNumber || inv.invoice_number || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{inv.seller?.nip || '—'}</TableCell>
                    <TableCell>{fmtAmt(inv.netAmount, inv.currency)}</TableCell>
                    <TableCell>{fmtAmt(inv.grossAmount, inv.currency)}</TableCell>
                    <TableCell>{fmtDate(inv.issueDate || inv.invoicingDate || inv.date)}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline"
                        onClick={() => handleDownload(inv.ksefNumber || inv.ksef_number, queryTokenId || null)}
                        disabled={downloading} data-testid="download-single-btn">
                        <CloudDownload className="h-3.5 w-3.5 mr-1" />{t('auto.misc2.download')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Manual download by reference */}
        <div className="border-t pt-3">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{t('auto.misc2.downloadByKsefReference')}</div>
          <div className="flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <Input
                placeholder="KSeF reference number (e.g. 1234567890-20260101-ABCDEF)"
                value={downloadRef}
                onChange={e => setDownloadRef(e.target.value)}
                data-testid="download-ref-input"
              />
            </div>
            {downloadTokens.length > 0 && (
              <Select value={downloadTokenId || '__auto__'} onValueChange={v => setDownloadTokenId(v === '__auto__' ? '' : v)}>
                <SelectTrigger className="h-9 w-40"><SelectValue placeholder={t('auto.misc2.autoToken')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__">{t('auto.misc2.autoSelect')}</SelectItem>
                  {downloadTokens.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button onClick={() => handleDownload(null, null)} disabled={downloading || !downloadRef} data-testid="download-ref-btn">
              <CloudDownload className="h-4 w-4 mr-1" />{downloading ? t('auto.misc2.downloading') : t('auto.misc2.download')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
